import {FeedbackData} from "../services/api/feedbackService";

interface FeedbackWithId extends FeedbackData {
  id: string;
  timestamp: any;
  userAgent?: string;
}

/**
 * Converts feedback data to CSV format
 * @param feedback Array of feedback items
 * @returns CSV string
 */
export const convertFeedbackToCSV = (feedback: FeedbackWithId[]): string => {
  if (feedback.length === 0) {
    return "No feedback data available";
  }

  // Define CSV headers
  const headers = [
    "ID",
    "Type",
    "Subject",
    "Message",
    "Name",
    "Email",
    "Site Location",
    "Expected Behaviour",
    "Observed Behaviour",
    "Screenshot URL",
    "Timestamp",
    "Read Status",
    "Read At",
    "User Agent",
  ];

  // Convert feedback data to CSV rows
  const rows = feedback.map((item) => {
    const formatDate = (timestamp: any): string => {
      if (!timestamp) return "";
      try {
        // Handle Firestore timestamp
        if (timestamp.toDate) {
          return timestamp.toDate().toISOString();
        }
        // Handle regular Date object
        if (timestamp instanceof Date) {
          return timestamp.toISOString();
        }
        // Handle timestamp number
        if (typeof timestamp === "number") {
          return new Date(timestamp).toISOString();
        }
        return String(timestamp);
      } catch (error) {
        return String(timestamp);
      }
    };

    const escapeCSVField = (field: any): string => {
      if (field === null || field === undefined) return "";
      const str = String(field);
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      if (str.includes(",") || str.includes("\n") || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    return [
      escapeCSVField(item.id),
      escapeCSVField(item.type),
      escapeCSVField(item.subject),
      escapeCSVField(item.message),
      escapeCSVField(item.name || ""),
      escapeCSVField(item.email || ""),
      escapeCSVField(item.siteLocation || ""),
      escapeCSVField(item.expectedBehaviour || ""),
      escapeCSVField(item.observedBehaviour || ""),
      escapeCSVField(item.screenshotUrl || ""),
      escapeCSVField(formatDate(item.timestamp)),
      escapeCSVField(item.read ? "Read" : "Unread"),
      escapeCSVField(formatDate(item.readAt)),
      escapeCSVField(item.userAgent || ""),
    ];
  });

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  return csvContent;
};

/**
 * Downloads CSV data as a file
 * @param csvContent CSV string content
 * @param filename Filename for the download
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  // Create blob with CSV content
  const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});

  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL object
  URL.revokeObjectURL(url);
};

/**
 * Generates filename with current date
 * @param prefix Filename prefix
 * @returns Generated filename
 */
export const generateCSVFilename = (prefix: string = "feedback"): string => {
  const now = new Date();
  const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format
  const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-"); // HH-MM-SS format
  return `${prefix}_${dateStr}_${timeStr}.csv`;
};
